import { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toPng } from "html-to-image";
import { Trophy, RotateCcw, Home as HomeIcon, Crown, Download } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { BOTS } from "@/data/mockData";
import { getBadgeDef } from "@/lib/storage";
import { PrizeTicket } from "@/components/PrizeTicket";
import { toast } from "sonner";

export default function Results() {
  const navigate = useNavigate();
  const location = useLocation();
  const { lastResult, profile, awardOffer } = useApp();
  const newlyEarned = location.state?.newlyEarned || [];
  const [wonOffer, setWonOffer] = useState(null);
  const awardedRef = useRef(false);

  const board = useMemo(() => {
    if (!lastResult) return [];
    const total = lastResult.total;
    const playerPoints = lastResult.score * 100;
    const bots = BOTS.map((b) => {
      const botScore = Math.floor(Math.random() * (total + 1));
      return { name: b.name, avatar: b.avatar, points: botScore * 100, isYou: false };
    });
    const rows = [...bots, { name: profile.name, avatar: null, points: playerPoints, isYou: true }];
    rows.sort((a, b) => b.points - a.points);
    return rows;
  }, [lastResult, profile]);

  const rank = board.findIndex((r) => r.isYou) + 1;
  const ticketRef = useRef(null);

  const saveTicket = async () => {
    if (!ticketRef.current) return;
    try {
      const dataUrl = await toPng(ticketRef.current, { pixelRatio: 2, cacheBust: true, skipFonts: true });
      const a = document.createElement("a");
      a.download = `appy-hour-offer-${wonOffer?.code || "ticket"}.png`;
      a.href = dataUrl;
      a.click();
      return true;
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    if (awardedRef.current || !lastResult) return;
    const prize = lastResult.prize;
    if (!prize || !prize.title) return;
    const met = prize.condition_type === "leaderboard" ? rank === 1 : lastResult.score >= (prize.min_score || 4);
    if (met) {
      awardedRef.current = true;
      const mins = Math.max(1, Number(prize.expiry_minutes) || 60);
      const expiresAt = new Date(Date.now() + mins * 60000).toISOString();
      const entry = awardOffer({
        title: prize.title,
        kind: prize.kind,
        venue: lastResult.venue,
        expiresAt,
      });
      setWonOffer(entry);
    }
  }, [lastResult, rank, awardOffer]);

  // Auto-save the ticket image once it has rendered.
  useEffect(() => {
    if (!wonOffer) return;
    const t = setTimeout(async () => {
      const ok = await saveTicket();
      if (ok) toast.success("Prize ticket saved to your downloads 🎟️");
    }, 600);
    return () => clearTimeout(t);
  }, [wonOffer]);

  if (!lastResult) {
    navigate("/");
    return null;
  }

  const { score, total, category } = lastResult;
  const pct = Math.round((score / total) * 100);

  const verdict = pct === 100 ? "Flawless! 🏆" : pct >= 60 ? "Solid round! 🍻" : pct >= 40 ? "Not bad! 🍺" : "Rough one… 😅";

  return (
    <div className="p-6 pb-10">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="text-center pt-8 mb-8"
      >
        <div className="inline-flex bg-amber-400 rounded-full p-5 mb-4 shadow-[0_0_35px_rgba(245,158,11,0.5)]">
          <Trophy size={44} className="text-slate-950" strokeWidth={2.5} />
        </div>
        <h1 className="font-display text-4xl font-black text-slate-50">{verdict}</h1>
        <p className="text-slate-400 mt-1">{category}</p>
      </motion.div>

      {wonOffer && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          data-testid="results-won-offer"
          className="mb-8"
        >
          <div className="flex items-center gap-2 mb-3 justify-center">
            <span className="text-xs font-black uppercase tracking-[0.2em] text-amber-400">🎉 You won a prize!</span>
          </div>
          <PrizeTicket ref={ticketRef} offer={wonOffer} />
          <div className="flex gap-3 mt-4">
            <button
              data-testid="results-save-ticket-btn"
              onClick={async () => { const ok = await saveTicket(); toast[ok ? "success" : "error"](ok ? "Ticket saved!" : "Couldn't save image."); }}
              className="flex-1 h-12 bg-slate-800 text-slate-100 rounded-2xl font-bold flex items-center justify-center gap-2 no-tap-highlight"
            >
              <Download size={18} /> Save ticket
            </button>
            <button
              data-testid="results-view-offers-btn"
              onClick={() => navigate("/offers")}
              className="flex-1 h-12 bg-amber-400 text-slate-950 rounded-2xl font-black flex items-center justify-center gap-2 no-tap-highlight"
            >
              My Offers
            </button>
          </div>
          <p className="text-xs text-slate-500 text-center mt-2">Auto-saved to your downloads. A countdown to expiry is now live.</p>
        </motion.div>
      )}


      <div className="grid grid-cols-3 gap-3 mb-8">
        <div className="bg-slate-900 rounded-2xl p-4 text-center border border-slate-800">
          <div className="font-display text-3xl font-black text-amber-400" data-testid="results-score">{score}/{total}</div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-1">Correct</div>
        </div>
        <div className="bg-slate-900 rounded-2xl p-4 text-center border border-slate-800">
          <div className="font-display text-3xl font-black text-cyan-400">{score * 100}</div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-1">Points</div>
        </div>
        <div className="bg-slate-900 rounded-2xl p-4 text-center border border-slate-800">
          <div className="font-display text-3xl font-black text-pink-400">#{rank}</div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-1">Rank</div>
        </div>
      </div>

      {newlyEarned.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900 border border-amber-500/30 rounded-2xl p-4 mb-8"
          data-testid="results-new-badges"
        >
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-amber-400 mb-3">New badges!</p>
          <div className="flex flex-wrap gap-3">
            {newlyEarned.map((id) => {
              const b = getBadgeDef(id);
              return (
                <div key={id} className="flex items-center gap-2 bg-slate-800 rounded-full pl-2 pr-4 py-1.5">
                  <span className="text-xl">{b.emoji}</span>
                  <span className="font-bold text-sm text-slate-100">{b.name}</span>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      <h2 className="font-display text-xl font-black text-slate-50 mb-4 flex items-center gap-2">
        <Crown size={20} className="text-amber-400" /> Leaderboard
      </h2>
      <div className="space-y-3 mb-8" data-testid="leaderboard">
        {board.map((r, i) => (
          <motion.div
            key={r.name + i}
            initial={{ opacity: 0, x: -15 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.06 }}
            className={`flex items-center gap-3 rounded-2xl p-3 border ${r.isYou ? "bg-amber-400/10 border-amber-400/40" : "bg-slate-900 border-slate-800"}`}
          >
            <span className="font-display font-black text-slate-500 w-6 text-center">{i + 1}</span>
            {r.avatar ? (
              <img src={r.avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
            ) : (
              <div className={`w-9 h-9 rounded-full flex items-center justify-center font-black text-sm ${r.isYou ? "bg-amber-400 text-slate-950" : "bg-slate-700 text-slate-300"}`}>
                {r.name[0].toUpperCase()}
              </div>
            )}
            <span className={`flex-1 font-bold ${r.isYou ? "text-amber-400" : "text-slate-200"}`}>
              {r.name}{r.isYou && " (You)"}
            </span>
            <span className="font-display font-black text-slate-50">{r.points}</span>
          </motion.div>
        ))}
      </div>

      <div className="flex gap-3">
        <motion.button
          data-testid="results-replay-btn"
          whileTap={{ scale: 0.96 }}
          onClick={() => navigate("/setup")}
          className="flex-1 h-14 bg-pink-500 text-white rounded-2xl font-display font-black flex items-center justify-center gap-2 no-tap-highlight"
        >
          <RotateCcw strokeWidth={2.5} size={20} /> Play Again
        </motion.button>
        <motion.button
          data-testid="results-home-btn"
          whileTap={{ scale: 0.96 }}
          onClick={() => navigate("/")}
          className="flex-1 h-14 bg-slate-800 text-slate-100 rounded-2xl font-display font-black flex items-center justify-center gap-2 no-tap-highlight"
        >
          <HomeIcon strokeWidth={2.5} size={20} /> Home
        </motion.button>
      </div>
    </div>
  );
}
